import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  FileText,
  Filter,
  Phone,
  RefreshCw,
  Search,
  Send,
  Smartphone,
  Users,
  X,
} from "lucide-react";

import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";
import WorkspaceTitleHeader from "../../components/common/WorkspaceTitleHeader";
import StandaloneMediaUploader from "../Campaigns/components/StandaloneMediaUploader";
import CompactTemplatePreviewCard from "../../components/CompactTemplatePreviewCard";

const TEMPLATE_PAGE_SIZE = 50;
const SYNC_ENDPOINT = businessId => `templates/sync/${businessId}`;

const HK = Object.freeze({
  None: "none",
  Image: "image",
  Video: "video",
  Document: "document",
});

const isMediaHeader = headerKind =>
  headerKind === HK.Image || headerKind === HK.Video || headerKind === HK.Document;

const mediaLabel = headerKind =>
  headerKind === HK.Image
    ? "Image URL"
    : headerKind === HK.Video
      ? "Video URL"
      : "Document URL";

const digitsOnly = value => String(value || "").replace(/\D/g, "");

const normalizeRecipient = value => {
  let normalized = digitsOnly(value);
  if (/^\d{10}$/.test(normalized)) normalized = `91${normalized}`;
  return normalized;
};

const isValidRecipient = value => /^\d{10,15}$/.test(String(value || ""));
const hasSameDigits = (a, b) => {
  const left = digitsOnly(a);
  const right = digitsOnly(b);
  return Boolean(left && right && left === right);
};
const normalizeNumberList = maybeList => {
  if (!Array.isArray(maybeList)) return [];
  const normalized = maybeList
    .map(normalizeRecipient)
    .filter(isValidRecipient);
  return [...new Set(normalized)];
};
const normalizeMetaProvider = value => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return normalized === "META" || normalized === "METACLOUD" || normalized === "META_CLOUD"
    ? "META_CLOUD"
    : "";
};

const getAvatarColor = name => {
  const colors = [
    "bg-emerald-100 text-emerald-700",
    "bg-blue-100 text-blue-700",
    "bg-violet-100 text-violet-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-cyan-100 text-cyan-700",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = name =>
  String(name || "")
    .split(" ")
    .map(n => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const getContactDisplayName = contact => {
  const name = String(contact?.name || "").trim();
  const profileName = String(contact?.profileName || contact?.ProfileName || "").trim();

  if (name && name !== "WhatsApp User") return name;
  if (profileName) return profileName;
  return name || "Unknown";
};

const findContactByNumber = (contacts, num) => {
  const targetDigits = digitsOnly(num);
  if (!targetDigits) return null;
  return contacts.find(contact => digitsOnly(contact?.phoneNumber) === targetDigits);
};

const normalizeHeaderKind = template => {
  const raw = String(template?.headerKind || template?.HeaderKind || "")
    .trim()
    .toLowerCase();
  if (raw === HK.Image || raw === HK.Video || raw === HK.Document) return raw;
  return HK.None;
};

const parseTemplateButtons = template => {
  const rawButtons =
    template?.buttonsJson ?? template?.buttons ?? template?.urlButtons ?? null;

  if (Array.isArray(rawButtons)) return rawButtons;
  if (typeof rawButtons === "string" && rawButtons.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(rawButtons);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const hasTemplateToken = value =>
  /\{\{\s*\d+\s*\}\}/.test(String(value || ""));

const isDynamicUrlButton = button => {
  const dynamicFlag =
    button?.isDynamic ??
    button?.IsDynamic ??
    button?.hasVariable ??
    button?.HasVariable ??
    button?.requiresParameter ??
    button?.RequiresParameter ??
    null;

  if (typeof dynamicFlag === "boolean") return dynamicFlag;

  const originalUrl = String(
    button?.ParameterValue ??
      button?.parameterValue ??
      button?.Url ??
      button?.url ??
      ""
  );

  return hasTemplateToken(originalUrl);
};

const normalizeMediaFilter = value => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "all") return "all";
  if (raw === "pdf" || raw === "doc") return "document";
  if (raw === "text" || raw === "image" || raw === "video" || raw === "document")
    return raw;
  return "all";
};

const hasValidationErrors = errors =>
  !!(
    errors.sender ||
    errors.recipient ||
    errors.template ||
    errors.headerMediaUrl ||
    (Array.isArray(errors.templateParams) && errors.templateParams.some(Boolean)) ||
    (Array.isArray(errors.urlButtonParams) && errors.urlButtonParams.some(Boolean))
  );

const extractApiErrorMessage = (payload, fallback = "") => {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload || fallback;

  return (
    payload.errorMessage ||
    payload.ErrorMessage ||
    payload.message ||
    payload.Message ||
    payload.error ||
    payload.Error ||
    payload.detail ||
    payload.Detail ||
    fallback
  );
};

function PhoneNumberTokenInput({ numbers, setNumbers, clearRecipientError }) {
  const [input, setInput] = useState("");

  const tryAddPhone = raw => {
    const normalized = normalizeRecipient(raw);
    if (!isValidRecipient(normalized)) return;
    if (numbers.some(existing => hasSameDigits(existing, normalized))) return;
    setNumbers([...numbers, normalized]);
    clearRecipientError();
  };

  const handleInputChange = event => {
    const value = event.target.value;
    const lastChar = value.slice(-1);
    const isSeparator = /[\s,]/.test(lastChar);

    if (isSeparator) {
      tryAddPhone(input.trim());
      setInput("");
      return;
    }

    setInput(value);
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      tryAddPhone(input.trim());
      setInput("");
      return;
    }

    if (event.key === "Tab") {
      tryAddPhone(input.trim());
      setInput("");
      return;
    }

    if (event.key === "Backspace" && input === "" && numbers.length > 0) {
      setNumbers(numbers.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (!input.trim()) return;
    tryAddPhone(input.trim());
    setInput("");
  };

  const removeNumber = number => {
    setNumbers(numbers.filter(n => !hasSameDigits(n, number)));
  };

  return (
    <div className="min-h-[50px] w-full cursor-text flex flex-wrap gap-2 rounded-[8px] border border-slate-200 bg-slate-50/80 p-2 transition-all focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-500/15">
      {numbers.map((num, idx) => (
        <span
          key={`${num}-${idx}`}
          className="flex items-center gap-1 rounded-[10px] border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm"
        >
          {num}
          <button
            type="button"
            className="text-slate-400 transition-colors hover:text-red-500"
            onClick={() => removeNumber(num)}
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        type="text"
        className="min-w-[120px] flex-grow bg-transparent p-1 text-sm text-slate-700 outline-none placeholder:text-slate-400"
        placeholder="Type number and press space/tab/enter"
        value={input}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </div>
  );
}

export default function SendTemplateMessageInstantPage() {
  const navigate = useNavigate();
  const { businessId: contextBusinessId } = useAuth();
  const businessId = useMemo(
    () => contextBusinessId || localStorage.getItem("businessId") || null,
    [contextBusinessId]
  );

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [manualNumbers, setManualNumbers] = useState([]);
  const [saveContact, setSaveContact] = useState(false);
  const [manualContactName, setManualContactName] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [senders, setSenders] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState("");
  const [sendersLoading, setSendersLoading] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [templateQuery, setTemplateQuery] = useState("");
  const [templateSort, setTemplateSort] = useState("created_desc");
  const [templateMedia, setTemplateMedia] = useState("all");
  const [selectedTemplateOption, setSelectedTemplateOption] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState("");
  const [headerMediaFile, setHeaderMediaFile] = useState(null);
  const [urlButtonParams, setUrlButtonParams] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  const templateQueryDebounceRef = useRef(null);
  const formRestoreAttemptedRef = useRef(false);
  const skipNextPersistRef = useRef(true);
  const pendingTemplateRestoreRef = useRef(null);
  const headerMediaPreviewRef = useRef("");

  const updateHeaderMediaPreviewUrl = useCallback(nextValue => {
    const next = String(nextValue || "");
    const prev = headerMediaPreviewRef.current;
    headerMediaPreviewRef.current = next;
    setHeaderMediaPreviewUrl(next);

    if (prev && prev !== next && prev.startsWith("blob:")) {
      setTimeout(() => {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          // no-op
        }
      }, 0);
    }
  }, []);

  useEffect(() => {
    return () => {
      const current = headerMediaPreviewRef.current;
      if (current && current.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(current);
        } catch {
          // no-op
        }
      }
    };
  }, []);

  const formStateStorageKey = useMemo(() => {
    if (!businessId) return null;
    return `sendTemplateInstant.formState.${businessId}`;
  }, [businessId]);

  const clearRecipientError = useCallback(() => {
    setErrors(prev => (prev.recipient ? { ...prev, recipient: null } : prev));
  }, []);

  const resolveContactNameForSave = useCallback(num => {
    const contact = findContactByNumber(contacts, num);
    const displayName = getContactDisplayName(contact);
    if (
      displayName &&
      displayName !== "WhatsApp User" &&
      displayName !== "Unknown"
    ) {
      return displayName;
    }

    if (
      manualNumbers.length === 1 &&
      digitsOnly(manualNumbers[0]) === digitsOnly(num)
    ) {
      const manual = manualContactName.trim();
      return manual || undefined;
    }

    return undefined;
  }, [contacts, manualContactName, manualNumbers]);

  const recipientNumbers = useMemo(() => {
    const merged = [...manualNumbers, ...selectedNumbers]
      .map(normalizeRecipient)
      .filter(isValidRecipient);
    return [...new Set(merged)];
  }, [manualNumbers, selectedNumbers]);

  const selectedSender = useMemo(
    () => senders.find(sender => sender.id === selectedSenderId) || null,
    [senders, selectedSenderId]
  );

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;

    return contacts.filter(contact => {
      const name = getContactDisplayName(contact).toLowerCase();
      const number = String(contact?.phoneNumber || "");
      return name.includes(q) || number.includes(q);
    });
  }, [contacts, contactSearch]);

  const toggleSelectedContactNumber = useCallback(phone => {
    const normalizedPhone = normalizeRecipient(phone);
    if (!isValidRecipient(normalizedPhone)) return;

    setSelectedNumbers(prev =>
      prev.some(n => hasSameDigits(n, normalizedPhone))
        ? prev.filter(n => !hasSameDigits(n, normalizedPhone))
        : [...prev, normalizedPhone]
    );
    clearRecipientError();
  }, [clearRecipientError]);

  const sortParams = useMemo(() => {
    switch (templateSort) {
      case "name_asc":
        return { sortKey: "name", sortDir: "asc" };
      case "name_desc":
        return { sortKey: "name", sortDir: "desc" };
      case "created_asc":
        return { sortKey: "createdAt", sortDir: "asc" };
      case "created_desc":
      default:
        return { sortKey: "createdAt", sortDir: "desc" };
    }
  }, [templateSort]);

  const templateOptions = useMemo(
    () =>
      templates
        .map(template => {
          const name = template?.name ?? template?.Name;
          const languageCode = template?.languageCode ?? template?.LanguageCode ?? "en_US";
          if (!name) return null;
          return {
            value: `${name}::${languageCode}`,
            label: name,
            name,
            languageCode,
            category: template?.category ?? template?.Category ?? "",
          };
        })
        .filter(Boolean),
    [templates]
  );

  const templateOptionsForSelect = useMemo(() => {
    if (!selectedTemplateOption) return templateOptions;
    const deduped = templateOptions.filter(
      option => option.value !== selectedTemplateOption.value
    );
    return [selectedTemplateOption, ...deduped];
  }, [selectedTemplateOption, templateOptions]);

  const dynamicUrlButtonSlots = useMemo(() => {
    if (!selectedTemplate?.buttonParams?.length) return [];
    return selectedTemplate.buttonParams
      .map((button, index) =>
        isDynamicUrlButton(button)
          ? {
              index,
              label:
                button?.Text ||
                button?.text ||
                `Dynamic URL Button ${index + 1}`,
            }
          : null
      )
      .filter(Boolean);
  }, [selectedTemplate]);

  const fetchApprovedTemplates = useCallback(async () => {
    if (!businessId) return;
    if (sendersLoading) return;
    if (!selectedSender) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);

    try {
      const params = {
        status: "APPROVED",
        q: templateQuery.trim() || undefined,
        provider: normalizeMetaProvider(selectedSender?.provider) || undefined,
        phoneNumberId: selectedSender?.phoneNumberId || undefined,
        media: normalizeMediaFilter(templateMedia) !== "all"
          ? normalizeMediaFilter(templateMedia)
          : undefined,
        page: 1,
        pageSize: TEMPLATE_PAGE_SIZE,
        sortKey: sortParams.sortKey,
        sortDir: sortParams.sortDir,
      };
      const response = await axiosClient.get(`templates/${businessId}`, { params });
      if (response?.data?.success) {
        setTemplates(Array.isArray(response.data.templates) ? response.data.templates : []);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      setTemplates([]);
      toast.error("Failed to load approved templates.");
    } finally {
      setLoadingTemplates(false);
    }
  }, [
    businessId,
    sendersLoading,
    selectedSender,
    templateQuery,
    templateMedia,
    sortParams.sortKey,
    sortParams.sortDir,
  ]);

  useEffect(() => {
    const loadContacts = async () => {
      setContactsLoading(true);
      try {
        const response = await axiosClient.get("/contacts/");
        setContacts(response?.data?.data?.items || []);
      } catch (error) {
        toast.error("Failed to load contacts.");
      } finally {
        setContactsLoading(false);
      }
    };

    loadContacts();
  }, []);

  useEffect(() => {
    if (!businessId) return;

    const loadSenders = async () => {
      setSendersLoading(true);
      try {
        const response = await axiosClient.get(`WhatsAppSettings/senders/${businessId}`);
        const raw = Array.isArray(response?.data)
          ? response.data
          : response?.data?.items || [];

        const normalized = raw
          .map(item => {
            const provider = normalizeMetaProvider(item?.provider || item?.Provider);
            const phoneNumberId = String(
              item?.phoneNumberId ?? item?.PhoneNumberId ?? ""
            ).trim();
            if (!provider || !phoneNumberId) return null;

            const displayNumber =
              item?.whatsAppBusinessNumber ||
              item?.whatsappBusinessNumber ||
              item?.displayNumber ||
              item?.phoneNumber ||
              item?.WhatsAppBusinessNumber ||
              item?.PhoneNumber ||
              phoneNumberId;

            return {
              id: item?.id ?? item?.Id ?? `${provider}|${phoneNumberId}`,
              provider,
              phoneNumberId,
              displayNumber: String(displayNumber || phoneNumberId),
              isDefault: !!(item?.isDefault ?? item?.IsDefault),
            };
          })
          .filter(Boolean);

        setSenders(normalized);
        if (!normalized.length) {
          setSelectedSenderId("");
          return;
        }

        if (normalized.length === 1) {
          setSelectedSenderId(normalized[0].id);
        } else {
          setSelectedSenderId(prev => {
            if (prev && normalized.some(sender => sender.id === prev)) return prev;
            const defaultSender = normalized.find(sender => sender.isDefault);
            return defaultSender?.id || normalized[0].id;
          });
        }
      } catch {
        setSenders([]);
        setSelectedSenderId("");
      } finally {
        setSendersLoading(false);
      }
    };

    loadSenders();
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    if (sendersLoading) return;
    if (templateQueryDebounceRef.current) clearTimeout(templateQueryDebounceRef.current);

    templateQueryDebounceRef.current = setTimeout(() => {
      fetchApprovedTemplates();
    }, 250);

    return () => {
      if (templateQueryDebounceRef.current) clearTimeout(templateQueryDebounceRef.current);
    };
  }, [businessId, fetchApprovedTemplates, sendersLoading]);

  const handleSyncTemplates = async () => {
    if (!businessId) return;
    setSyncing(true);
    try {
      const response = await axiosClient.post(SYNC_ENDPOINT(businessId));
      if (response?.data?.success || response?.status === 200) {
        toast.success("Templates synced.");
        await fetchApprovedTemplates();
      } else {
        toast.error("Template sync failed.");
      }
    } catch {
      toast.error("Error syncing templates.");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTemplateSelect = useCallback(async (option, restoredState = null) => {
    if (!businessId) return;
    if (sendersLoading || !selectedSender) return;
    setSelectedTemplateOption(option || null);

    if (!option?.name) {
      setSelectedTemplate(null);
      setTemplateParams([]);
      setUrlButtonParams([]);
      setHeaderMediaUrl("");
      updateHeaderMediaPreviewUrl("");
      setHeaderMediaFile(null);
      return;
    }

    try {
      const response = await axiosClient.get(
        `templates/${businessId}/${encodeURIComponent(option.name)}`,
        {
          params: {
            language: option.languageCode || undefined,
            provider: normalizeMetaProvider(selectedSender?.provider) || undefined,
            phoneNumberId: selectedSender?.phoneNumberId || undefined,
          },
        }
      );

      const rawTemplate = response?.data?.template || response?.data || null;
      const templateName = rawTemplate?.name ?? rawTemplate?.Name;
      if (!templateName) {
        toast.error("Could not load template details.");
        return;
      }

      const body = rawTemplate?.body ?? rawTemplate?.Body ?? "";
      const headerKind = normalizeHeaderKind(rawTemplate);
      const buttonParams = parseTemplateButtons(rawTemplate);
      const parametersCount = (body.match(/{{[0-9]+}}/g) || []).length;
      const requiresHeaderMediaUrl =
        rawTemplate?.requiresHeaderMediaUrl === true ||
        rawTemplate?.RequiresMediaHeader === true ||
        isMediaHeader(headerKind);

      const normalizedTemplate = {
        name: templateName,
        language:
          rawTemplate?.language ??
          rawTemplate?.Language ??
          rawTemplate?.languageCode ??
          rawTemplate?.LanguageCode ??
          "en_US",
        body,
        headerKind,
        requiresHeaderMediaUrl,
        parametersCount,
        buttonParams,
      };

      const defaultTemplateParams = Array(parametersCount).fill("");
      const defaultUrlButtonParams = buttonParams
        .filter(button => isDynamicUrlButton(button))
        .map(() => "");

      let restoredTemplateParams = defaultTemplateParams;
      let restoredUrlButtonParams = defaultUrlButtonParams;
      let restoredHeaderMediaUrl = "";

      const canRestore =
        restoredState &&
        restoredState.templateName === normalizedTemplate.name &&
        (!restoredState.templateLanguage ||
          restoredState.templateLanguage === normalizedTemplate.language);

      if (canRestore) {
        if (
          Array.isArray(restoredState.templateParams) &&
          restoredState.templateParams.length === defaultTemplateParams.length
        ) {
          restoredTemplateParams = restoredState.templateParams.map(value =>
            String(value || "")
          );
        }

        if (
          Array.isArray(restoredState.urlButtonParams) &&
          restoredState.urlButtonParams.length === defaultUrlButtonParams.length
        ) {
          restoredUrlButtonParams = restoredState.urlButtonParams.map(value =>
            String(value || "")
          );
        }

        if (typeof restoredState.headerMediaUrl === "string") {
          restoredHeaderMediaUrl = restoredState.headerMediaUrl;
        }
      }

      setSelectedTemplate(normalizedTemplate);
      setTemplateParams(restoredTemplateParams);
      setHeaderMediaUrl(restoredHeaderMediaUrl);
      updateHeaderMediaPreviewUrl(restoredHeaderMediaUrl);
      setHeaderMediaFile(null);
      setUrlButtonParams(restoredUrlButtonParams);
      setErrors({});
    } catch (error) {
      toast.error("Failed to load template details.");
    }
  }, [businessId, selectedSender, sendersLoading, updateHeaderMediaPreviewUrl]);

  useEffect(() => {
    if (sendersLoading) return;
    if (!selectedSender) {
      setSelectedTemplateOption(null);
      setSelectedTemplate(null);
      setTemplateParams([]);
      setUrlButtonParams([]);
      setHeaderMediaUrl("");
      updateHeaderMediaPreviewUrl("");
      setHeaderMediaFile(null);
      setErrors({});
      return;
    }
    if (!selectedTemplateOption) return;
    const stillAvailable = templateOptions.some(
      option => option.value === selectedTemplateOption.value
    );
    if (stillAvailable) return;

    setSelectedTemplateOption(null);
    setSelectedTemplate(null);
    setTemplateParams([]);
    setUrlButtonParams([]);
    setHeaderMediaUrl("");
    updateHeaderMediaPreviewUrl("");
    setHeaderMediaFile(null);
    setErrors({});
  }, [selectedSender, selectedTemplateOption, sendersLoading, templateOptions, updateHeaderMediaPreviewUrl]);

  useEffect(() => {
    if (!formStateStorageKey) return;
    if (formRestoreAttemptedRef.current) return;
    formRestoreAttemptedRef.current = true;

    try {
      const raw = sessionStorage.getItem(formStateStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);

      if (typeof parsed.contactSearch === "string") {
        setContactSearch(parsed.contactSearch);
      }
      if (typeof parsed.templateQuery === "string") {
        setTemplateQuery(parsed.templateQuery);
      }
      if (typeof parsed.templateSort === "string") {
        setTemplateSort(parsed.templateSort);
      }
      if (typeof parsed.templateMedia === "string") {
        setTemplateMedia(parsed.templateMedia);
      }
      if (typeof parsed.saveContact === "boolean") {
        setSaveContact(parsed.saveContact);
      }
      if (typeof parsed.manualContactName === "string") {
        setManualContactName(parsed.manualContactName);
      }

      setManualNumbers(normalizeNumberList(parsed.manualNumbers));
      setSelectedNumbers(normalizeNumberList(parsed.selectedNumbers));

      if (parsed.selectedTemplateName) {
        const languageCode = parsed.selectedTemplateLanguage || "en_US";
        setSelectedTemplateOption({
          value: `${parsed.selectedTemplateName}::${languageCode}`,
          label: parsed.selectedTemplateName,
          name: parsed.selectedTemplateName,
          languageCode,
        });

        pendingTemplateRestoreRef.current = {
          templateName: parsed.selectedTemplateName,
          templateLanguage: languageCode,
          headerMediaUrl:
            typeof parsed.headerMediaUrl === "string" ? parsed.headerMediaUrl : "",
          templateParams: Array.isArray(parsed.templateParams) ? parsed.templateParams : [],
          urlButtonParams: Array.isArray(parsed.urlButtonParams)
            ? parsed.urlButtonParams
            : [],
        };
      }
    } catch {
      // no-op
    }
  }, [formStateStorageKey]);

  useEffect(() => {
    if (sendersLoading || !selectedSender) return;
    const pending = pendingTemplateRestoreRef.current;
    if (!pending?.templateName) return;

    pendingTemplateRestoreRef.current = null;
    const option = {
      value: `${pending.templateName}::${pending.templateLanguage || "en_US"}`,
      label: pending.templateName,
      name: pending.templateName,
      languageCode: pending.templateLanguage || "en_US",
    };
    handleTemplateSelect(option, pending);
  }, [handleTemplateSelect, selectedSender, sendersLoading]);

  useEffect(() => {
    if (!formStateStorageKey) return;
    if (!formRestoreAttemptedRef.current) return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    try {
      sessionStorage.setItem(
        formStateStorageKey,
        JSON.stringify({
          contactSearch,
          templateQuery,
          templateSort,
          templateMedia,
          saveContact,
          manualContactName,
          manualNumbers,
          selectedNumbers,
          selectedTemplateName:
            selectedTemplateOption?.name || selectedTemplate?.name || null,
          selectedTemplateLanguage:
            selectedTemplateOption?.languageCode || selectedTemplate?.language || null,
          headerMediaUrl,
          templateParams,
          urlButtonParams,
        })
      );
    } catch {
      // no-op
    }
  }, [
    formStateStorageKey,
    contactSearch,
    templateQuery,
    templateSort,
    templateMedia,
    saveContact,
    manualContactName,
    manualNumbers,
    selectedNumbers,
    selectedTemplateOption,
    selectedTemplate,
    headerMediaUrl,
    templateParams,
    urlButtonParams,
  ]);

  const buildValidationErrors = useCallback(() => {
    const nextErrors = {};
    if (!selectedSender) {
      nextErrors.sender = "Select a valid sender number before sending.";
    }

    if (!recipientNumbers.length) {
      nextErrors.recipient =
        "Add at least one valid recipient using manual entry or contacts.";
    }

    if (!selectedTemplate) {
      nextErrors.template = "Select an approved template.";
      return nextErrors;
    }

    if (isMediaHeader(selectedTemplate.headerKind) && !headerMediaUrl.trim()) {
      nextErrors.headerMediaUrl = `${mediaLabel(selectedTemplate.headerKind)} is required.`;
    }

    const paramErrors = templateParams.map(value => !String(value || "").trim());
    if (paramErrors.some(Boolean)) {
      nextErrors.templateParams = paramErrors;
    }

    const buttonErrors = urlButtonParams.map(value => !String(value || "").trim());
    if (buttonErrors.some(Boolean)) {
      nextErrors.urlButtonParams = buttonErrors;
    }

    return nextErrors;
  }, [
    headerMediaUrl,
    recipientNumbers.length,
    selectedSender,
    selectedTemplate,
    templateParams,
    urlButtonParams,
  ]);

  const canSend = useMemo(
    () => !submitting && !hasValidationErrors(buildValidationErrors()),
    [buildValidationErrors, submitting]
  );

  const clearSendState = useCallback(() => {
    setManualNumbers([]);
    setSelectedNumbers([]);
    setSaveContact(false);
    setManualContactName("");
    setSelectedTemplateOption(null);
    setSelectedTemplate(null);
    setTemplateParams([]);
    setHeaderMediaUrl("");
    updateHeaderMediaPreviewUrl("");
    setHeaderMediaFile(null);
    setUrlButtonParams([]);
    setErrors({});

    if (formStateStorageKey) {
      try {
        sessionStorage.removeItem(formStateStorageKey);
      } catch {
        // no-op
      }
    }
  }, [formStateStorageKey, updateHeaderMediaPreviewUrl]);

  const handleSend = useCallback(async () => {
    const nextErrors = buildValidationErrors();
    setErrors(nextErrors);
    if (hasValidationErrors(nextErrors)) return;
    if (!selectedTemplate) return;

    const urlParamsByButtonIndex = {};
    dynamicUrlButtonSlots.forEach((slot, slotIndex) => {
      urlParamsByButtonIndex[slot.index] = String(urlButtonParams[slotIndex] || "").trim();
    });

    const sharedPayload = {
      templateName: selectedTemplate.name,
      languageCode: selectedTemplate.language || "en_US",
      templateParameters: templateParams.map(value => String(value || "").trim()),
      headerKind: selectedTemplate.headerKind || HK.None,
      headerMediaUrl: isMediaHeader(selectedTemplate.headerKind)
        ? headerMediaUrl.trim()
        : null,
      urlButtonParams: (selectedTemplate.buttonParams || []).map((button, index) => {
        if (!isDynamicUrlButton(button)) return "";
        return urlParamsByButtonIndex[index] || "";
      }),
      templateBody: selectedTemplate.body || "",
      provider: normalizeMetaProvider(selectedSender?.provider) || "META_CLOUD",
      phoneNumberId: selectedSender?.phoneNumberId || null,
      deliveryMode: "Immediate",
      startConversation: true,
    };

    setSubmitting(true);
    let success = 0;
    let failed = 0;
    let firstFailureMessage = "";
    let hasNon429Failure = false;
    try {
      for (const recipientNumber of recipientNumbers) {
        try {
          const contactName =
            saveContact ? resolveContactNameForSave(recipientNumber) : undefined;
          const response = await axiosClient.post("/messageengine/send-template-simple", {
            ...sharedPayload,
            recipientNumber,
            isSaveContact: saveContact,
            contactName,
          }, {
            __silentGenericToast: true,
          });
          const isSuccess = response?.data?.success === true || response?.status === 200;
          if (isSuccess) {
            success += 1;
          } else {
            failed += 1;
            if (!firstFailureMessage) {
              firstFailureMessage = extractApiErrorMessage(
                response?.data,
                "Template send failed."
              );
            }
          }
        } catch (error) {
          failed += 1;
          if (error?.response?.status === 429) {
            continue;
          }
          hasNon429Failure = true;
          if (!firstFailureMessage) {
            firstFailureMessage = extractApiErrorMessage(
              error?.response?.data,
              error?.message || "Template send failed."
            );
          }
        }
      }

      if (success > 0 && failed === 0) {
        toast.success(`Template sent to ${success} recipient${success > 1 ? "s" : ""}.`);
        clearSendState();
      } else if (success > 0 && failed > 0) {
        const summary = `Sent: ${success}, Failed: ${failed}`;
        if (hasNon429Failure) {
          toast.warn(firstFailureMessage ? `${summary}. ${firstFailureMessage}` : summary);
        }
      } else {
        if (hasNon429Failure) {
          toast.error(firstFailureMessage || "Template send failed.");
        }
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    buildValidationErrors,
    clearSendState,
    dynamicUrlButtonSlots,
    headerMediaUrl,
    recipientNumbers,
    resolveContactNameForSave,
    saveContact,
    selectedSender,
    selectedTemplate,
    templateParams,
    urlButtonParams,
  ]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-slate-100 px-8 pb-6 pt-6 font-sans text-[14px] leading-6 text-slate-800 antialiased sm:px-10">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-4">
        <WorkspaceTitleHeader
          icon={FileText}
          title="Send Template Message"
          subtitle="Send approved WhatsApp templates instantly to one or more recipients."
          actions={
            <button
              type="button"
              onClick={() => navigate("/app/messaging")}
              className="inline-flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
            >
              <ArrowLeft size={16} />
              Back
            </button>
          }
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-8 xl:col-span-9">
            <section className="space-y-4 rounded-[10px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                    <FileText size={18} className="text-emerald-600" />
                    Template Setup
                  </h3>
                </div>
                <div className="inline-flex items-center rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
                  {recipientNumbers.length} recipients
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
                {/* Single Row Configuration */}
                <div className="flex-grow space-y-2">
                  <label className="block text-xs font-medium text-slate-600">
                    Select approved template
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        inputId="templateSelect"
                        classNamePrefix="react-select"
                        isClearable
                        isLoading={loadingTemplates || syncing}
                        options={templateOptionsForSelect}
                        value={selectedTemplateOption}
                        placeholder="Search approved templates..."
                        onChange={handleTemplateSelect}
                        onInputChange={(value, meta) => {
                          if (meta.action === "input-change") setTemplateQuery(value);
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: (base, state) => ({
                            ...base,
                            minHeight: 40,
                            borderRadius: 8,
                            borderColor: errors.template
                              ? "#fca5a5"
                              : state.isFocused
                                ? "#34d399"
                                : "#e2e8f0",
                            boxShadow: state.isFocused
                              ? "0 0 0 3px rgba(16,185,129,0.12)"
                              : "none",
                            backgroundColor: state.isDisabled ? "#f8fafc" : "#f8fafc",
                            "&:hover": {
                              borderColor: state.isFocused ? "#34d399" : "#cbd5e1",
                            },
                          }),
                          placeholder: base => ({
                            ...base,
                            color: "#94a3b8",
                          }),
                          singleValue: base => ({
                            ...base,
                            color: "#334155",
                          }),
                        }}
                      />
                    </div>

                    <div className="flex gap-1" ref={filterRef}>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsFilterOpen(!isFilterOpen)}
                          className={`h-[40px] w-[40px] shrink-0 rounded-[10px] border flex items-center justify-center transition-all ${
                            isFilterOpen || templateSort !== "created_desc" || templateMedia !== "all"
                              ? "bg-emerald-50 border-emerald-500 text-emerald-600 shadow-sm"
                              : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-white"
                          }`}
                          title="Filter and Sort"
                        >
                          <Filter className="h-4 w-4" />
                        </button>

                        {isFilterOpen && (
                          <div className="absolute right-0 z-[100] mt-2 w-56 space-y-3 rounded-[10px] border border-slate-200 bg-white p-3 shadow-xl">
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Sort By</label>
                              <select
                                value={templateSort}
                                onChange={event => setTemplateSort(event.target.value)}
                                className="w-full h-9 rounded-[8px] border border-slate-200 bg-slate-50 px-3 text-xs text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                              >
                                <option value="created_desc">Newest First</option>
                                <option value="created_asc">Oldest First</option>
                                <option value="name_asc">Name A-Z</option>
                                <option value="name_desc">Name Z-A</option>
                              </select>
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Media Type</label>
                              <select
                                value={templateMedia}
                                onChange={event => setTemplateMedia(event.target.value)}
                                className="w-full h-9 rounded-[8px] border border-slate-200 bg-slate-50 px-3 text-xs text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                              >
                                <option value="all">All Media</option>
                                <option value="text">Text</option>
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                                <option value="document">Document (PDF)</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={handleSyncTemplates}
                        disabled={syncing}
                        className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[10px] border border-slate-200 bg-slate-50 text-slate-600 transition-colors hover:border-emerald-200 hover:bg-white hover:text-emerald-600 disabled:opacity-50"
                        title="Sync templates from Meta"
                      >
                        <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                      </button>
                    </div>
                  </div>
                  {errors.template ? (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.template}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-2 xl:w-[280px] xl:shrink-0">
                  <label className="block text-xs font-medium text-slate-600">
                    Sender (From)
                  </label>
                  <select
                    value={selectedSenderId}
                    onChange={event => {
                      setSelectedSenderId(event.target.value);
                      if (errors.sender) setErrors(prev => ({ ...prev, sender: null }));
                    }}
                    className={`h-[40px] w-full rounded-[8px] border bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none ${
                      errors.sender
                        ? "border-red-300 focus:ring-2 focus:ring-red-100"
                        : "border-slate-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/15"
                    }`}
                  >
                    <option value="">Select sender</option>
                    {senders.map(sender => (
                      <option key={sender.id} value={sender.id}>
                        {sender.displayNumber} ({sender.provider})
                      </option>
                    ))}
                  </select>
                  {errors.sender ? (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.sender}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-4 border-t border-slate-100 pt-4">
                {/* 50/50 split for recipients */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-[10px] border border-slate-200 bg-slate-50/60 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="flex items-center gap-2 font-semibold text-slate-800">
                        <Users size={18} className="text-emerald-600" />
                        Select Contacts
                      </h4>
                      <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-[10px]">
                        {selectedNumbers.length} selected
                      </span>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={contactSearch}
                        onChange={event => setContactSearch(event.target.value)}
                        placeholder="Search name or number"
                        className="w-full rounded-[8px] border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-all focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/15"
                      />
                    </div>
                    <div className="mt-3 h-[300px] overflow-y-auto rounded-[10px] border border-slate-200 bg-white p-2">
                      {contactsLoading ? (
                        <div className="p-4 text-sm text-slate-500">Loading contacts...</div>
                      ) : filteredContacts.length === 0 ? (
                        <div className="p-4 text-sm text-slate-500">No contacts found.</div>
                      ) : (
                        filteredContacts.map(contact => {
                          const phone = String(contact?.phoneNumber || "").trim();
                          const isSelected = selectedNumbers.some(n => hasSameDigits(n, phone));
                          const canSelectContact = isValidRecipient(normalizeRecipient(phone));
                          const displayName = getContactDisplayName(contact);

                          return (
                            <button
                              type="button"
                              key={contact.id || phone || `${displayName}-${digitsOnly(phone)}`}
                              aria-pressed={isSelected}
                              disabled={!canSelectContact}
                              onMouseDown={event => event.preventDefault()}
                              onClick={() => toggleSelectedContactNumber(phone)}
                              className={`group mx-1 my-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-[10px] border border-transparent p-2.5 text-left transition-all ${
                                !canSelectContact
                                  ? "cursor-not-allowed opacity-60"
                                  : "cursor-pointer"
                              } ${
                                isSelected
                                  ? "bg-emerald-50 border-emerald-100"
                                  : canSelectContact
                                    ? "hover:bg-slate-50"
                                    : ""
                              }`}
                            >
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                                  isSelected
                                    ? "bg-emerald-500 text-white shadow-md scale-105"
                                    : getAvatarColor(displayName || "?")
                                }`}
                              >
                                {isSelected ? (
                                  <CheckCircle size={16} />
                                ) : (
                                  getInitials(displayName || "?")
                                )}
                              </div>
                              <div className="min-w-0">
                                <p
                                  className={`text-sm font-medium truncate ${
                                    isSelected ? "text-emerald-900" : "text-slate-700"
                                  }`}
                                >
                                  {displayName}
                                </p>
                                <p className="truncate text-xs text-slate-500">{phone}</p>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="rounded-[10px] border border-slate-200 bg-slate-50/60 p-4">
                    <div className="mb-3">
                      <h4 className="flex items-center gap-2 font-semibold text-slate-800">
                        <Phone size={18} className="text-emerald-600" />
                        Manual Entry
                      </h4>
                    </div>
                    <PhoneNumberTokenInput
                      numbers={manualNumbers}
                      setNumbers={setManualNumbers}
                      clearRecipientError={clearRecipientError}
                    />
                    <div className="mt-3 flex items-start gap-2">
                      <div className="relative flex h-5 items-center">
                        <input
                          id="saveTemplateContact"
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          checked={saveContact}
                          onChange={event => setSaveContact(event.target.checked)}
                        />
                      </div>
                      <label
                        htmlFor="saveTemplateContact"
                        className="cursor-pointer select-none text-xs leading-tight text-slate-500"
                      >
                        Automatically save new manual numbers to your Contacts list after sending.
                      </label>
                    </div>
                    {saveContact && manualNumbers.length === 1 && recipientNumbers.length === 1 ? (
                      <div className="mt-3">
                        <label className="mb-1 block text-xs text-slate-500">
                          Contact name (optional)
                        </label>
                        <input
                          type="text"
                          value={manualContactName}
                          onChange={event => setManualContactName(event.target.value)}
                          placeholder="Leave empty to use WhatsApp profile name if available"
                          className="w-full rounded-[8px] border border-slate-200 px-3 py-2 text-sm transition-all focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/15"
                        />
                      </div>
                    ) : null}
                    <p className="mt-3 text-[11px] text-slate-500">
                      Numbers auto-normalize to digits; 10-digit input is prefixed with 91.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-1">
                <p className="text-xs text-slate-600">
                  Selected recipients: <strong>{recipientNumbers.length}</strong>
                </p>
                {errors.recipient ? (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.recipient}
                  </p>
                ) : null}

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={!canSend}
                    className={`inline-flex items-center gap-2 rounded-[10px] px-6 py-2.5 text-sm font-semibold transition ${
                      canSend
                        ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-200 hover:from-emerald-600 hover:to-teal-700"
                        : "cursor-not-allowed bg-slate-100 text-slate-400"
                    }`}
                  >
                    {submitting ? (
                      <>
                        <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Template Message
                        <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </section>

            <section className="space-y-4 rounded-[10px] border border-slate-200 bg-white p-5 shadow-sm">
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                  <FileText size={18} className="text-emerald-600" />
                  Template Parameters
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Fill any body placeholders or media fields required by the selected template.
                </p>
              </div>

              {!selectedTemplate ? (
                <p className="text-sm text-slate-500">
                  Select a template to configure required parameters.
                </p>
              ) : (
                <div className="space-y-4">
                  {isMediaHeader(selectedTemplate.headerKind) ? (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {mediaLabel(selectedTemplate.headerKind)}
                      </label>
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={headerMediaUrl.startsWith("handle:") ? "" : headerMediaUrl}
                          onChange={event => {
                            const next = event.target.value;
                            setHeaderMediaUrl(next);
                            updateHeaderMediaPreviewUrl(next);
                            setHeaderMediaFile(null);
                            if (errors.headerMediaUrl) {
                              setErrors(prev => ({ ...prev, headerMediaUrl: null }));
                            }
                          }}
                          placeholder="Enter HTTPS URL or upload below..."
                          disabled={headerMediaUrl.startsWith("handle:")}
                          className={`w-full rounded-[8px] border bg-slate-50 px-3 py-2 text-sm outline-none transition ${
                            errors.headerMediaUrl
                              ? "border-red-300 focus:ring-2 focus:ring-red-100"
                              : "border-slate-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/15"
                            }`}
                        />

                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-slate-200" />
                          </div>
                          <div className="relative flex justify-center text-[10px] uppercase">
                            <span className="bg-white px-2 text-slate-400 font-medium tracking-wider">
                              Or Upload File
                            </span>
                          </div>
                        </div>

                        <StandaloneMediaUploader
                          mediaType={selectedTemplate.headerKind.toUpperCase()}
                          handle={headerMediaUrl}
                          onUploaded={handle => {
                            setHeaderMediaUrl(handle);
                            if (errors.headerMediaUrl) {
                              setErrors(prev => ({ ...prev, headerMediaUrl: null }));
                            }
                          }}
                          onPreview={previewUrl => {
                            updateHeaderMediaPreviewUrl(previewUrl || "");
                            if (errors.headerMediaUrl) {
                              setErrors(prev => ({ ...prev, headerMediaUrl: null }));
                            }
                          }}
                          onFileSelected={file => {
                            setHeaderMediaFile(
                              file
                                ? {
                                    name: file.name,
                                    type: file.type,
                                    size: file.size,
                                  }
                                : null
                            );
                          }}
                        />

                        {headerMediaUrl.startsWith("handle:") ? (
                          <button
                            type="button"
                            onClick={() => {
                              setHeaderMediaUrl("");
                              updateHeaderMediaPreviewUrl("");
                              setHeaderMediaFile(null);
                            }}
                            className="text-[10px] text-red-500 hover:text-red-700 font-medium flex items-center gap-1 mt-1"
                          >
                            <X size={10} />
                            Remove uploaded media
                          </button>
                        ) : null}
                      </div>
                      {errors.headerMediaUrl ? (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3.5 w-3.5" />
                          {errors.headerMediaUrl}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {templateParams.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {templateParams.map((value, index) => (
                        <div key={`template-param-${index}`}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Body Parameter {"{{"}
                            {index + 1}
                            {"}}"}
                          </label>
                          <input
                            type="text"
                            value={value}
                            onChange={event => {
                              const next = [...templateParams];
                              next[index] = event.target.value;
                              setTemplateParams(next);
                              if (errors.templateParams?.[index]) {
                                const nextErrorFlags = [...(errors.templateParams || [])];
                                nextErrorFlags[index] = false;
                                setErrors(prev => ({ ...prev, templateParams: nextErrorFlags }));
                              }
                            }}
                            className={`w-full rounded-[8px] border bg-slate-50 px-3 py-2 text-sm outline-none transition ${
                              errors.templateParams?.[index]
                                ? "border-red-300 focus:ring-2 focus:ring-red-100"
                                : "border-slate-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/15"
                            }`}
                            placeholder={`Enter value for {{${index + 1}}}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No body parameters required.</p>
                  )}

                  {dynamicUrlButtonSlots.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-slate-700">
                        Dynamic URL button parameters
                      </p>
                      {dynamicUrlButtonSlots.map((slot, slotIndex) => (
                        <div key={`url-button-param-${slot.index}`}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            {slot.label}
                          </label>
                          <input
                            type="text"
                            value={urlButtonParams[slotIndex] || ""}
                            onChange={event => {
                              const next = [...urlButtonParams];
                              next[slotIndex] = event.target.value;
                              setUrlButtonParams(next);
                              if (errors.urlButtonParams?.[slotIndex]) {
                                const nextErrorFlags = [...(errors.urlButtonParams || [])];
                                nextErrorFlags[slotIndex] = false;
                                setErrors(prev => ({ ...prev, urlButtonParams: nextErrorFlags }));
                              }
                            }}
                            className={`w-full rounded-[8px] border bg-slate-50 px-3 py-2 text-sm outline-none transition ${
                              errors.urlButtonParams?.[slotIndex]
                                ? "border-red-300 focus:ring-2 focus:ring-red-100"
                                : "border-slate-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/15"
                            }`}
                            placeholder="Enter URL placeholder value"
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          </div>

          <div className="lg:col-span-4 xl:col-span-3">
            <div className="sticky top-6 ml-auto w-full max-w-[360px] overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Preview
                </h3>
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Smartphone className="h-3.5 w-3.5" />
                  WhatsApp View
                </span>
              </div>
              <div className="flex min-h-[460px] items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100/80 p-2.5">
                {selectedTemplate ? (
                  <div className="w-full px-2">
                    <CompactTemplatePreviewCard
                      templateBody={selectedTemplate.body}
                      parameters={templateParams}
                      headerMediaUrl={headerMediaUrl}
                      headerMediaPreviewUrl={headerMediaPreviewUrl}
                      headerKind={selectedTemplate.headerKind}
                      buttons={selectedTemplate.buttonParams}
                      templateName={selectedTemplate.name}
                      headerMediaFileName={headerMediaFile?.name || ""}
                      headerMediaMimeType={headerMediaFile?.type || ""}
                    />
                  </div>
                ) : (
                  <div className="text-center text-slate-500 px-6">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm font-medium">No template selected</p>
                    <p className="text-xs mt-1">
                      Choose a template to render an instant preview.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
